import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { label: "Destinations", href: "#destinations" },
  { label: "Services", href: "#services" },
  { label: "Story", href: "#story" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#inquiry" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
      // ensure we land at the top after navigation
      setTimeout(() => window.scrollTo({ top: 0, behavior: "auto" }), 50);
    }
    setOpen(false);
  };

  const handleNavLink = (e, hash) => {
    e.preventDefault();
    setOpen(false);
    const id = hash.replace("#", "");
    const doScroll = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    if (location.pathname === "/") {
      doScroll();
    } else {
      // Route to landing then scroll to anchor
      navigate("/" + hash);
      setTimeout(doScroll, 150);
    }
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: "spring", stiffness: 80, damping: 14 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all ${
        scrolled ? "py-2" : "py-4"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className={`flex items-center justify-between bg-white border-2 border-[#282829] rounded-full px-5 py-2.5 ${
          scrolled ? "brutal-shadow-red-sm" : ""
        }`}>
          <a href="/" onClick={handleLogoClick} className="flex items-center gap-2.5 cursor-pointer" data-testid="navbar-logo" aria-label="Go to home">
            <img
              src="https://customer-assets.emergentagent.com/job_shah-edu-3d/artifacts/k9mfo0sl_image.png"
              alt="Shah Overseas Education — Study Abroad Consultants in Nagpur"
              className="h-11 w-auto object-contain"
              loading="eager"
              fetchpriority="high"
              decoding="async"
            />
          </a>

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => handleNavLink(e, l.href)}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="px-3.5 py-1.5 text-sm font-semibold rounded-full hover:bg-[#282829] hover:text-white transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <a
            href="#inquiry"
            onClick={(e) => handleNavLink(e, "#inquiry")}
            data-testid="navbar-cta-button"
            className="hidden md:inline-flex items-center gap-2 bg-[#c92e34] text-white font-bold text-sm px-5 py-2 rounded-full border-2 border-[#282829] hover:bg-[#282829] transition-colors"
          >
            Book Counselling
          </a>

          <button
            onClick={() => setOpen(!open)}
            className="md:hidden w-10 h-10 grid place-items-center rounded-full border-2 border-[#282829]"
            data-testid="navbar-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden mt-2 bg-white border-2 border-[#282829] rounded-3xl p-4 brutal-shadow-red-sm"
          >
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => handleNavLink(e, l.href)}
                className="block py-2 px-3 font-bold border-b border-[#282829]/10 last:border-0"
                data-testid={`nav-mobile-${l.label.toLowerCase()}`}
              >
                {l.label}
              </a>
            ))}
            <a
              href="#inquiry"
              onClick={(e) => handleNavLink(e, "#inquiry")}
              className="mt-3 block text-center bg-[#c92e34] text-white font-bold py-2.5 rounded-full border-2 border-[#282829]"
              data-testid="navbar-mobile-cta"
            >
              Book Counselling
            </a>
          </motion.div>
        )}
      </div>
    </motion.header>
  );
}
